import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AuthProvider } from './contexts/AuthContext'
import { ErrorBoundary } from './components/ErrorBoundary'
import { Layout } from './components/Layout'
import { ProtectedRoute } from './components/ProtectedRoute'
import { AdminRoute } from './components/AdminRoute'
import { OfflineBanner } from './components/OfflineBanner'
import { ROUTES } from './utils/constants'

// Lazy load pages for code splitting
import { lazy, Suspense } from 'react'
import { Loading } from './components/ui/Spinner'

// Public pages
const Login = lazy(() => import('./pages/Login'))
const Signup = lazy(() => import('./pages/Signup'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'))

// Authenticated pages
const Home = lazy(() => import('./pages/Home'))
const TournamentDetail = lazy(() => import('./pages/TournamentDetail'))
const LineupPicker = lazy(() => import('./pages/LineupPicker'))
const MyLineups = lazy(() => import('./pages/MyLineups'))
const CareerLeaderboard = lazy(() => import('./pages/CareerLeaderboard'))
const Profile = lazy(() => import('./pages/Profile'))
const ProfileEdit = lazy(() => import('./pages/ProfileEdit'))
const UserDetail = lazy(() => import('./pages/UserDetail'))

// Admin pages
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'))
const AdminTournamentCreate = lazy(() => import('./pages/admin/TournamentCreate'))
const AdminTournamentEdit = lazy(() => import('./pages/admin/TournamentEdit'))
const AdminApproveLineups = lazy(() => import('./pages/admin/ApproveLineups'))
const AdminEmail = lazy(() => import('./pages/admin/SendEmail'))
const AdminAllUsers = lazy(() => import('./pages/admin/AllUsers'))
const AdminSidePotLab = lazy(() => import('./pages/admin/SidePotLab'))

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <OfflineBanner />
          <Toaster position="top-right" richColors />

          <Suspense fallback={<Loading />}>
            <Routes>
              {/* Public routes */}
              <Route path={ROUTES.LOGIN} element={<Login />} />
              <Route path={ROUTES.SIGNUP} element={<Signup />} />
              <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
              <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
              <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmail />} />

              {/* Public tournament detail - no auth required for sharing */}
              <Route
                path="/tournaments/:id"
                element={
                  <Layout>
                    <ErrorBoundary>
                      <TournamentDetail />
                    </ErrorBoundary>
                  </Layout>
                }
              />

              {/* Public side pot lab - no auth required */}
              <Route
                path="/sidepot"
                element={
                  <Layout>
                    <ErrorBoundary>
                      <AdminSidePotLab />
                    </ErrorBoundary>
                  </Layout>
                }
              />

              {/* Protected routes */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ErrorBoundary>
                        <Routes>
                          <Route path={ROUTES.HOME} element={<Home />} />
                          <Route path="/tournaments/:id/pick" element={<LineupPicker />} />
                          <Route path={ROUTES.MY_LINEUPS} element={<MyLineups />} />
                          <Route path={ROUTES.CAREER_LEADERBOARD} element={<CareerLeaderboard />} />
                          <Route path={ROUTES.PROFILE} element={<Profile />} />
                          <Route path={ROUTES.PROFILE_EDIT} element={<ProfileEdit />} />
                          <Route path="/users/:id" element={<UserDetail />} />

                          {/* Admin routes */}
                          <Route
                            path={ROUTES.ADMIN_DASHBOARD}
                            element={
                              <AdminRoute>
                                <AdminDashboard />
                              </AdminRoute>
                            }
                          />
                          <Route
                            path={ROUTES.ADMIN_TOURNAMENT_CREATE}
                            element={
                              <AdminRoute>
                                <AdminTournamentCreate />
                              </AdminRoute>
                            }
                          />
                          <Route
                            path="/admin/tournaments/:id/edit"
                            element={
                              <AdminRoute>
                                <AdminTournamentEdit />
                              </AdminRoute>
                            }
                          />
                          <Route
                            path="/admin/tournaments/:id/approve"
                            element={
                              <AdminRoute>
                                <AdminApproveLineups />
                              </AdminRoute>
                            }
                          />
                          <Route
                            path={ROUTES.ADMIN_EMAIL}
                            element={
                              <AdminRoute>
                                <AdminEmail />
                              </AdminRoute>
                            }
                          />
                          <Route
                            path={ROUTES.ADMIN_USERS}
                            element={
                              <AdminRoute>
                                <AdminAllUsers />
                              </AdminRoute>
                            }
                          />

                          {/* 404 redirect */}
                          <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
                        </Routes>
                      </ErrorBoundary>
                    </Layout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}

export default App
